import {
  MessagesChat,
  MessagesCompose,
  MessagesInbox,
  MessagesRightPanel,
  MessagesTopBarActions,
  type MessagesComposeSendPayload,
  type MessagesInboxTab,
  type MsThread,
} from '@elva/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@elva/platform';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import {
  adaptPatientSearchToMsContact,
  adaptSmsConversation,
  adaptSmsThread,
} from '../adapters/messagesAdapters';
import { usePatchConversation, useSmsInbox, type SmsInboxFilter } from '../hooks/useSmsInbox';
import { useGenerateSummary, useSmsComposeSend, useSmsReply, useSmsThread } from '../hooks/useSmsThread';
import { normalizePatientSearchResults } from '../../patients/adapters/patientAdapters';
import { usePatientSearch } from '../../patients/hooks/usePatientSearch';

const EMPTY_THREAD: MsThread = {
  id: '',
  name: '',
  phone: '',
  initials: '',
  lang: 'en',
  when: '',
  unread: 0,
  currentTopic: null,
  topics: [],
  health: 'neutral',
  autoRespondOn: false,
  optedOut: false,
  preview: '',
  patient: {
    name: '',
    phone: '',
    initials: '',
    age: 0,
    gender: '',
    patientId: '',
    lang: 'en',
    status: '',
    next: null,
    balance: '$0',
    insurance: { plan: '', status: '', remaining: 0, expires: '' },
    lastVisit: null,
    consent: { sms: true, email: true },
  },
  summary: '',
  actions: [],
  opportunities: [],
  messages: [],
};

export function MessagesPage() {
  const [inboxTab, setInboxTab] = useState<MessagesInboxTab>('all');
  const isServerTab = inboxTab === 'awaiting' || inboxTab === 'archived';
  const serverFilter: SmsInboxFilter | undefined =
    inboxTab === 'awaiting'
      ? { awaiting_response: true }
      : inboxTab === 'archived'
        ? { status: 'archived' }
        : undefined;

  // Default (open) query drives All/Unread/Manual + their counts; the filtered
  // query only runs when an Awaiting/Archived tab is active.
  const defaultInbox = useSmsInbox();
  const filteredInbox = useSmsInbox(serverFilter, isServerTab);
  const activeInbox = isServerTab ? filteredInbox : defaultInbox;
  const { isLoading, isError } = activeInbox;

  const patchConversation = usePatchConversation();
  const authUser = useAuthStore((s) => s.user);
  const currentStaffName = useMemo(() => {
    if (!authUser) return '';
    return [authUser.name, authUser.last_name].filter(Boolean).join(' ').trim() || authUser.email;
  }, [authUser]);
  const conversations = useMemo(
    () => (activeInbox.data?.conversations ?? []).map(adaptSmsConversation),
    [activeInbox.data?.conversations],
  );
  const inboxCounts = useMemo(() => {
    const open = (defaultInbox.data?.conversations ?? []).map(adaptSmsConversation);
    return {
      all: open.length,
      unread: open.filter((c) => c.unread > 0).length,
      manual: open.filter((c) => !c.autoRespondOn && !c.optedOut).length,
    };
  }, [defaultInbox.data?.conversations]);

  const [activeId, setActiveId] = useState('');
  useEffect(() => {
    if (conversations.length > 0 && !conversations.some((c) => c.id === activeId)) {
      setActiveId(conversations[0]!.id);
    }
  }, [conversations, activeId]);

  const inboxThread = useMemo(
    () => conversations.find((t) => t.id === activeId) ?? conversations[0],
    [conversations, activeId],
  );

  const { data: threadData } = useSmsThread(activeId || null);
  const thread = useMemo(() => {
    if (!inboxThread) return EMPTY_THREAD;
    if (threadData) return adaptSmsThread(inboxThread, threadData);
    return inboxThread;
  }, [inboxThread, threadData]);

  const smsReply = useSmsReply(activeId || '');
  const smsComposeSend = useSmsComposeSend();
  const generateSummary = useGenerateSummary(activeId || '');
  const episodeId = thread.episodeId ?? null;

  const [composeQuery, setComposeQuery] = useState('');
  const patientSearch = usePatientSearch(composeQuery.trim().length >= 2 ? composeQuery.trim() : undefined);
  const composeContacts = useMemo(
    () =>
      normalizePatientSearchResults(patientSearch.data)
        .map(adaptPatientSearchToMsContact)
        .filter((contact): contact is NonNullable<typeof contact> => contact != null),
    [patientSearch.data],
  );

  const [autoOn, setAutoOn] = useState(thread.autoRespondOn);
  const [takenOver, setTakenOver] = useState(false);
  const [topic, setTopic] = useState('all');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMin, setComposeMin] = useState(false);

  useEffect(() => {
    setAutoOn(thread.autoRespondOn);
    setTakenOver(false);
    setTopic('all');
  }, [activeId, thread.autoRespondOn]);

  const handleSelect = useCallback(
    (id: string) => {
      setActiveId(id);
      const selected = conversations.find((c) => c.id === id);
      if (selected && selected.unread > 0) {
        void patchConversation
          .mutateAsync({ conversationId: id, action: 'mark_read' })
          .catch((err) => console.error(err));
      }
    },
    [conversations, patchConversation],
  );

  const handleTakeOver = useCallback(() => {
    if (!activeId) return;
    void patchConversation
      .mutateAsync({ conversationId: activeId, action: 'takeover' })
      .catch((err) => console.error(err));
  }, [activeId, patchConversation]);

  const handleSend = useCallback(
    (text: string) => {
      if (!activeId) return;
      void smsReply.mutateAsync(text).catch((err) => console.error(err));
    },
    [activeId, smsReply],
  );

  const handleComposeSend = useCallback(
    (payload: MessagesComposeSendPayload) => {
      const patientId = payload.contact?.id ? Number(payload.contact.id) : undefined;
      void smsComposeSend
        .mutateAsync({
          phone: payload.phone,
          text: payload.text,
          ...(patientId != null && !Number.isNaN(patientId) ? { patientId } : {}),
          conversations: defaultInbox.data?.conversations ?? [],
        })
        .then((conversationId) => {
          if (conversationId) setActiveId(conversationId);
          setComposeOpen(false);
          setComposeMin(false);
          setComposeQuery('');
        })
        .catch((err) => console.error(err));
    },
    [defaultInbox.data?.conversations, smsComposeSend],
  );

  usePageTopBar({
    actions: (
      <MessagesTopBarActions
        onNewMessage={() => {
          setComposeOpen(true);
          setComposeMin(false);
        }}
      />
    ),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
        <MessagesInbox
          activeId={activeId}
          onSelect={handleSelect}
          conversations={conversations}
          activeTab={inboxTab}
          onTabChange={setInboxTab}
          counts={inboxCounts}
          serverFiltered={isServerTab}
          loading={activeInbox.isLoading}
        />
        <ElvaSectionState isLoading={isLoading && conversations.length === 0} isError={isError}>
          <>
            <MessagesChat
              thread={thread}
              autoOn={autoOn}
              setAutoOn={setAutoOn}
              takenOver={takenOver}
              setTakenOver={setTakenOver}
              topic={topic}
              currentStaffName={currentStaffName}
              onTakeOver={handleTakeOver}
              onSend={handleSend}
            />
            <MessagesRightPanel
              thread={thread}
              topic={topic}
              setTopic={setTopic}
              summaryGenerating={generateSummary.isPending}
              {...(episodeId != null
                ? {
                    onGenerateSummary: () => {
                      void generateSummary.mutateAsync(episodeId).catch((err: unknown) => {
                        console.error(err);
                      });
                    },
                  }
                : {})}
            />
          </>
        </ElvaSectionState>
      </div>
      {composeOpen ? (
        <MessagesCompose
          minimized={composeMin}
          contacts={composeContacts}
          onRecipientQueryChange={setComposeQuery}
          onSend={handleComposeSend}
          sending={smsComposeSend.isPending}
          onMinimize={() => {
            setComposeMin(true);
          }}
          onRestore={() => {
            setComposeMin(false);
          }}
          onClose={() => {
            setComposeOpen(false);
            setComposeMin(false);
            setComposeQuery('');
          }}
        />
      ) : null}
    </div>
  );
}
